import moment from "moment";

import {
  ClientRequest,
  ReminderMode,
  RequestAnswer,
  RequestItem,
  RequestItemInput,
  RequestItemKind,
  RequestSave,
  RequestStatus,
} from "./clientRequests.types";

/* Rules of client request lists. The server checks the same limits (backend ClientRequestRules.cs). */

export const MAX_ITEMS = 30;
export const MAX_LABEL = 300;
export const MAX_TITLE = 150;
export const MAX_MESSAGE = 2000;
export const MAX_ANSWER = 4000;
export const MAX_REMINDERS = 6;
export const NEEDS_DB_UPDATE = "Client requests need a database update first.";

export const ITEM_KINDS: {
  key: RequestItemKind;
  label: string;
  hint: string;
}[] = [
  { key: "file", label: "File", hint: "Your client uploads one or more files" },
  { key: "question", label: "Question", hint: "Your client types an answer" },
  { key: "yesno", label: "Yes / No", hint: "Your client picks Yes or No" },
];

export const kindLabel = (kind: RequestItemKind) =>
  ITEM_KINDS.find((k) => k.key === kind)?.label ?? kind;

export const REMINDER_MODES: {
  key: ReminderMode;
  label: string;
  hint: string;
}[] = [
  {
    key: "gentle",
    label: "Gentle",
    hint: "3 days before the due date, on the due date, then every 3 days",
  },
  { key: "firm", label: "Firm", hint: "Every 2 days" },
  { key: "custom", label: "Custom", hint: "Every few days, you choose" },
  { key: "off", label: "Off", hint: "No automatic reminders" },
];

/** "Gentle: 3 days before the due date, ..." in words, for the tab and the window. */
export const reminderText = (
  mode: ReminderMode,
  everyDays: number,
  hasDueDate: boolean,
): string => {
  switch (mode) {
    case "gentle":
      return hasDueDate
        ? "3 days before the due date, on the due date, then every 3 days"
        : "Every 3 days";
    case "firm":
      return "Every 2 days";
    case "custom":
      return everyDays === 1 ? "Every day" : `Every ${everyDays} days`;
    default:
      return "No automatic reminders";
  }
};

/** A new blank item. */
export const newItem = (kind: RequestItemKind = "file"): RequestItemInput => ({
  id: null,
  kind,
  label: "",
  fileIfYes: false,
});

/** Moves an item up (-1) or down (+1); returns a new list. */
export const moveItem = <T>(items: T[], index: number, by: -1 | 1): T[] => {
  const to = index + by;
  if (to < 0 || to >= items.length) return items;
  const next = items.slice();
  const [it] = next.splice(index, 1);
  next.splice(to, 0, it);
  return next;
};

/** Why the window can't be saved (or sent), or null. Same rules as the server. */
export const requestFormProblem = (
  form: {
    title: string;
    message?: string;
    items: RequestItemInput[];
    reminderMode: ReminderMode;
    reminderEveryDays: number;
  },
  sending: boolean,
): string | null => {
  const title = form.title.trim();
  if (!title)
    return 'Enter a title your client will see, for example "October documents".';
  if (title.length > MAX_TITLE)
    return `The title can be at most ${MAX_TITLE} characters.`;
  if ((form.message || "").trim().length > MAX_MESSAGE)
    return `The message can be at most ${MAX_MESSAGE} characters.`;
  if (form.items.length > MAX_ITEMS)
    return `A request list can have at most ${MAX_ITEMS} items.`;
  if (sending && form.items.length === 0)
    return "Add at least one item before sending.";
  for (let i = 0; i < form.items.length; i++) {
    const label = form.items[i].label.trim();
    if (!label) return `Item ${i + 1}: write what you need from your client.`;
    if (label.length > MAX_LABEL)
      return `Item ${i + 1}: use at most ${MAX_LABEL} characters.`;
  }
  if (
    form.reminderMode === "custom" &&
    (!Number.isInteger(form.reminderEveryDays) ||
      form.reminderEveryDays < 1 ||
      form.reminderEveryDays > 30)
  )
    return "Remind every 1 to 30 days.";
  return null;
};

/** The POST / PUT body from the window's form. */
export const requestPayload = (
  workId: string,
  form: {
    title: string;
    message?: string;
    dueDate?: string | null;
    items: RequestItemInput[];
    reminderMode: ReminderMode;
    reminderEveryDays: number;
    moveToReadyWhenDone: boolean;
    repeatWithWork: boolean;
  },
  send: boolean,
): RequestSave => ({
  workId,
  title: form.title.trim(),
  message: (form.message || "").trim() || null,
  dueDate: form.dueDate || null,
  items: form.items.map((i) => ({
    id: i.id || null,
    kind: i.kind,
    label: i.label.trim(),
    fileIfYes: i.kind === "yesno" && i.fileIfYes,
  })),
  reminderMode: form.reminderMode,
  reminderEveryDays:
    form.reminderMode === "custom" ? form.reminderEveryDays : 0,
  moveToReadyWhenDone: form.moveToReadyWhenDone,
  repeatWithWork: form.repeatWithWork,
  send,
});

/** Items for a template or "Suggest from last time" (no ids: they become new items). */
export const itemsAsNew = (items: RequestItemInput[]): RequestItemInput[] =>
  items.map((i) => ({
    id: null,
    kind: i.kind,
    label: i.label,
    fileIfYes: i.kind === "yesno" && i.fileIfYes,
  }));

/** "2 of 4" and a 0-100 percentage. */
export const progressOf = (
  request: Pick<ClientRequest, "doneCount" | "totalCount">,
) => {
  const total = request.totalCount || 0;
  const done = Math.min(request.doneCount || 0, total);
  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    label: `${done} of ${total}`,
  };
};

export const STATUS_PILL: Record<
  RequestStatus,
  { label: string; color: string; bg: string }
> = {
  draft: { label: "Draft", color: "#5B6678", bg: "#EEF0F4" },
  sent: { label: "Sent", color: "#2F6FED", bg: "#EAF1FE" },
  complete: { label: "Complete", color: "#12805C", bg: "#E6F6EE" },
  cancelled: { label: "Cancelled", color: "#9A3412", bg: "#FFF1E8" },
};

/** The line under a list in the tab: next reminder, done date, etc. */
export const statusLine = (r: ClientRequest, now = moment()): string => {
  switch (r.status) {
    case "draft":
      return r.copiedFromEarlier
        ? "Draft copied from the last time · ready to send"
        : "Draft · not sent yet";
    case "complete":
      return `Everything received${
        r.completedAt ? ` on ${moment(r.completedAt).format("MMM D")}` : ""
      }`;
    case "cancelled":
      return `Cancelled${
        r.cancelledAt ? ` on ${moment(r.cancelledAt).format("MMM D")}` : ""
      }`;
    default: {
      const parts = [
        `Sent${r.sentAt ? ` ${moment(r.sentAt).format("MMM D")}` : ""}${
          r.sentByName ? ` by ${r.sentByName}` : ""
        }`,
      ];
      if (r.dueDate)
        parts.push(
          `due ${moment(r.dueDate, "YYYY-MM-DD").format("MMM D")}${
            moment(r.dueDate, "YYYY-MM-DD").isBefore(now, "day")
              ? " (overdue)"
              : ""
          }`,
        );
      if (r.reminderMode === "off") parts.push("no automatic reminders");
      else if (r.nextReminderAt)
        parts.push(
          `next reminder ${moment(r.nextReminderAt).format("MMM D")} (${
            r.remindersSent
          } of ${r.maxReminders || MAX_REMINDERS} sent)`,
        );
      else if (r.firmNotified)
        parts.push(`all ${r.maxReminders || MAX_REMINDERS} reminders sent`);
      return parts.join(" · ");
    }
  }
};

/** What the firm sees as the client's answer to one item. */
export const answerText = (item: RequestItem): string | null => {
  if (item.kind === "question") return item.answer?.trim() || null;
  if (item.kind === "yesno") {
    if (item.yesNo === true) return "Yes";
    if (item.yesNo === false) return "No";
    return null;
  }
  return null;
};

/** Whether the item wants a file right now (file item, or "Yes" with "if yes, upload a file"). */
export const wantsFile = (
  item: Pick<RequestItem, "kind" | "yesNo" | "fileIfYes">,
) =>
  item.kind === "file" ||
  (item.kind === "yesno" && item.fileIfYes && item.yesNo === true);

/** Whether an item is done with the client's not-yet-sent answers (portal). Same rule as the server. */
export const isItemDone = (
  item: Pick<RequestItem, "kind" | "fileIfYes" | "files">,
  answer: string | null | undefined,
  yesNo: boolean | null | undefined,
): boolean => {
  const files = item.files.length;
  if (item.kind === "file") return files > 0;
  if (item.kind === "question") return !!(answer || "").trim();
  if (item.kind === "yesno")
    return (
      yesNo === false || (yesNo === true && (!item.fileIfYes || files > 0))
    );
  return false;
};

/** The portal's "Send to my accountant" body: only answers that changed. */
export const answersPayload = (
  items: RequestItem[],
  answers: Record<string, string>,
  choices: Record<string, boolean | null>,
): RequestAnswer[] =>
  items.flatMap((item): RequestAnswer[] => {
    if (item.kind === "question") {
      const value = answers[item.id];
      if (value === undefined || value.trim() === (item.answer || "").trim())
        return [];
      return [{ itemId: item.id, answer: value.trim() }];
    }
    if (item.kind === "yesno") {
      const value = choices[item.id];
      if (value === undefined || value === null || value === item.yesNo)
        return [];
      return [{ itemId: item.id, yesNo: value }];
    }
    return [];
  });

/** "Sarah Chen will be invited to your client portal" etc. */
export const recipientsLine = (
  people: { name: string; email: string; status: string }[],
): string => {
  if (people.length === 0) return "";
  const invite = people.filter((p) => p.status === "invite");
  if (invite.length)
    return `${
      invite[0].name || invite[0].email
    } will be invited to your client portal and will see this list there.`;
  const names = people.map((p) => p.name || p.email);
  return names.length === 1
    ? `Sent to ${names[0]} by email, with a link to your client portal.`
    : `Sent to ${names.slice(0, -1).join(", ")} and ${
        names[names.length - 1]
      } by email, with a link to your client portal.`;
};
